import React, { useEffect, useState } from 'react';

import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { useSetSettlementDate } from '@/hooks/trades';
import { canPerform } from '@/features/trades/hooks';
import { formatDate, todayLocalKey } from '@/utils/format';
import { getClosureScheduleNote } from '@/utils/trades/closure-schedule';
import type { HolidayItem, PositionDetail } from '@/types/trades.types';

interface SettlementDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
  /** Detail-key-invalidating onSuccess wrapper from the shared hook. */
  onSuccess?: () => void;
  /**
   * The `useSetSettlementDate` mutation handle + the active-holiday list,
   * both composed by the page-level `useTradeActions` (LR-208 r3 M4). The
   * holidays array is `useHolidays(enableHolidays)` already unwrapped and
   * gated by the dialog-open flag at the call site, so the modal does not
   * re-subscribe.
   */
  setSettlementDate: ReturnType<typeof useSetSettlementDate>;
  holidays?: HolidayItem[];
}

// LR-208 r5 M1: local-zone day key, NOT new Date().toISOString().split('T')[0]
// (UTC slice silently bumps the wire date a day forward from UTC-negative
// evenings). Helper from @/utils/format mirrors activity-grouping.dayKeyOf.
const today = todayLocalKey;

/**
 * Set-Close-Date dialog for the detail-page action bar (LR-576 rename — it
 * stamps the CLOSE settlement date). Built on the shared
 * `ConfirmDialog` (which IS @headlessui — no new lib) so it stays in visual +
 * a11y parity with the proven TradesPage Set-Close-Date block. Adds the
 * soft amber holiday warning via `useHolidays` (filtered locally; SOFT — never
 * disables Confirm). The submit re-checks `canPerform` against the freshly
 * loaded trade (ACTN-02) so a stale/keyboard/race submit no-ops.
 */
export function SettlementDateModal({
  isOpen,
  onClose,
  trade,
  onSuccess,
  setSettlementDate,
  holidays,
}: SettlementDateModalProps) {
  const { mutate, isPending, error, reset } = setSettlementDate;
  // LR-208 r6 M10: default to TODAY, not trade.settlement_date. settlement_date
  // maps to the backend open_settlement_date, which for every status this dialog
  // opens on (active / matured) is already in the
  // PAST — seeding it makes the happy-path Confirm fail the backend's
  // no-past-date guard with a confusing 400. Mirrors the LR-411 TradesPage fix.
  const [actualSettlementDate, setActualSettlementDate] = useState<string>(today());
  const [reason, setReason] = useState<string>('');

  // LR-208 r6 M11: constrain the picker — the backend rejects a past date AND a
  // close date before the open settlement date (close >= open). min = max(today,
  // open_settlement_date), using the local-zone today() (not the UTC slice) so
  // the floor matches the trader's clock. Mirrors the LR-412 TradesPage guard.
  //
  // LR-702: on an ACTIVE trade with a known end, maturity joins the max — a
  // close date before it can never be the day the trade settles (closure runs
  // only on `matured`, and `effective_close_date` clamps up to maturity), so
  // storing one makes the displayed date a lie. Mirrors SetCloseDateDialog; a
  // floor that disagreed between the two dialogs is how the first one drifted.
  const minDate = (() => {
    const t = today();
    const candidates = [t];
    const openDate = trade.settlement_date;
    if (openDate) candidates.push(openDate);
    if (trade.status === 'active' && trade.maturity_date) {
      candidates.push(trade.maturity_date);
    }
    return candidates.reduce((a, b) => (b > a ? b : a));
  })();

  // Holidays come pre-fetched from the page-level useTradeActions (gated by
  // the dialog-open flag). The page already filters to is_active; the second
  // filter here is a defensive belt against a future caller passing an
  // unfiltered list (forward-compat / no-op today).
  const matchedHoliday = holidays?.find((h) => h.is_active && h.date === actualSettlementDate);

  // LR-610: on an `active` trade this date is a plan, not a settlement. Shared
  // with the TradesPage Set-Close-Date block so both dialogs say the same thing.
  const scheduleNote = getClosureScheduleNote(trade, actualSettlementDate);

  // Every visible note describes the field, in render order.
  const describedBy = [
    matchedHoliday ? 'settlement-holiday' : null,
    scheduleNote ? 'settlement-schedule' : null,
    'settlement-preview',
  ].filter((id): id is string => id !== null).join(' ');

  // Reset on (re)open so a prior error / stale value never bleeds into a fresh
  // attempt (mirrors the TradesPage handle* reset pattern).
  useEffect(() => {
    if (isOpen) {
      reset();
      setActualSettlementDate(today());
      setReason('');
    }
  }, [isOpen, reset]);

  const handleConfirm = () => {
    // Submit-path guard (ACTN-02 / PITFALLS #7): re-check validity against the
    // freshly-loaded trade before mutating; no-op on a stale/keyboard/race path.
    if (!canPerform(trade, 'set_settlement_date')) return;
    // LR-208 r6 (LOW): a native date input can be cleared to ''. Don't fire a
    // guaranteed-400 mutation with an empty actual_settlement_date.
    if (!actualSettlementDate) return;
    mutate(
      { id: trade.id, actualSettlementDate, reason },
      { onSuccess: () => { onSuccess?.(); onClose(); } },
    );
  };

  return (
    <ConfirmDialog
      show={isOpen}
      onClose={onClose}
      // LR-576 (feedback п.70): the dialog stamps the CLOSE settlement date —
      // named after the LR-406 blotter column so it never reads as open-side.
      title="Set Close Date"
      confirmLabel="Confirm"
      isLoading={isPending}
      onConfirm={handleConfirm}
      // No enabled-but-silent Confirm: a cleared date visibly disables it, matching
      // the sibling Amend/PartialRecall modals (LR-478 review r8). matchedHoliday is a
      // SOFT warning, so it deliberately does NOT gate. handleConfirm keeps the guard.
      confirmDisabled={!actualSettlementDate}
      error={error}
      errorFallback="Failed to set close date. Please try again."
    >
      <p className="text-sm text-gray-500">
        Set the actual close settlement date for position <strong>{trade.id}</strong> ({trade.symbol_id}).
      </p>
      <div className="mt-4">
        <label htmlFor="actual_settlement_date" className="block text-xs text-gray-500 mb-1">
          Close Settlement Date
        </label>
        <input
          type="date"
          id="actual_settlement_date"
          value={actualSettlementDate}
          min={minDate}
          onChange={(e) => setActualSettlementDate(e.target.value)}
          // Always describe the field with the before→after preview (+ the holiday
          // warning when shown), mirroring the sibling Amend/PartialRecall modals so
          // a SR user hears the constraint/after-state on focus (LR-478 review r8).
          aria-describedby={describedBy}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
        {matchedHoliday && (
          <div
            id="settlement-holiday"
            className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
            aria-live="polite"
          >
            {actualSettlementDate} is a public holiday{matchedHoliday.name ? `: ${matchedHoliday.name}` : ''}.
            Consider another date.
          </div>
        )}
        {scheduleNote && (
          <div
            id="settlement-schedule"
            className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
            aria-live="polite"
          >
            {scheduleNote}
          </div>
        )}
        {/* LR-478 S3: before→after preview of the actual settlement date so the
            change is self-explanatory before commit. aria-live so SR users hear
            the after-date update as they pick (LR-478 review r1 #4). */}
        <p id="settlement-preview" className="mt-2 text-[11px] text-gray-600" aria-live="polite">
          Close settlement date:{' '}
          <span className="text-gray-500">
            {trade.actual_settlement_date ? formatDate(trade.actual_settlement_date) : 'not set'}
          </span>
          {' → '}
          <span className="font-semibold text-gray-900">
            {actualSettlementDate ? formatDate(actualSettlementDate) : '—'}
          </span>
        </p>
      </div>
      <div className="mt-4">
        <label htmlFor="settlement_reason" className="block text-xs text-gray-500 mb-1">
          Reason (optional)
        </label>
        <textarea
          id="settlement_reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Standard T+3 settlement"
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
          rows={2}
        />
      </div>
    </ConfirmDialog>
  );
}
